// Merchant dashboard — three Polaris cards per IMPLEMENTATION_PLAN §3.8:
//   1. Events delivered today (bar chart of 24h hourly buckets, per dest)
//   2. Meta event match quality (stub for now; real integration is a
//      separate Facebook Business OAuth flow)
//   3. Data health (green/yellow/red with per-check breakdown)
//
// All three cards poll their backend endpoints independently on an interval
// so a single failing endpoint doesn't take down the whole dashboard.
//
// Intentionally simple visualization: a row of div bars for the hourly chart
// rather than a charting library. Polaris ships no chart primitive and pulling
// in Recharts just for this would be ~50KB of JS. Bars-as-divs is accessible,
// renders instantly, and scales fine for 24 points.

import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useAppBridge } from "@shopify/app-bridge-react";
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Layout,
  InlineGrid,
  Divider,
  Badge,
  Button,
  Collapsible,
  Spinner,
  Banner,
} from "@shopify/polaris";
import { apiFetch, getCached, type Shopify } from "./api";
import { ActivityFeed } from "./Activity";
import { waitingRequests, type DataRequestList } from "./data-requests";
import { formatDate } from "./dates";
import {
  DESTINATION_DISPLAY_NAMES,
  type DestinationType,
} from "./destination-catalog";
import { allTypesFrom } from "./available-types";
import {
  connectMoreCard,
  eventsFlowingLabel,
  paywallCopy,
  serverCaughtSummary,
  type ServerCaughtCounts,
} from "./dashboard-copy";

// Backend-evaluated entitlement. The Dashboard only renders the banner;
// the actual gating happens on the API and workers. See
// src/lib/billing-entitlement.ts.
interface EntitlementInfo {
  entitled: boolean;
  reason: string;
  blocked: boolean;
}

interface DestinationView {
  // DestinationType, not a hand-written union. A copy that stayed at six
  // while the API returned a seventh would not fail to compile: the type maps
  // keyed off it return undefined for the new one, and the merchant sees
  // "undefined is in test mode".
  type: DestinationType;
  enabled: boolean;
  configured: boolean;
  // This service's dry-run flag for the type (events logged, not sent).
  // Optional so preview/mocks don't have to set it; the API always sends it.
  dryRun?: boolean;
  lastSuccessAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------- Shared polling hook ----------
// Poll every `intervalMs` for JSON from `path`. Leaves state intact on
// transient errors so a momentary 502 doesn't blank the card.
//
// SSE is the primary signal now: each card subscribes to a shared
// `streamTick` counter (incremented by useDashboardStream on every
// recordDelivery in any worker) and refetches immediately on bump.
// Polling stays at a relaxed 30s as a safety net for when the SSE
// connection is dropped (mobile networks, idle proxies, etc.) or
// when no events have flowed at all so a tick never fires.
function usePoll<T>(
  shopify: Shopify,
  path: string,
  intervalMs = 30_000,
  streamTick = 0,
): { data: T | null; error: string | null; loading: boolean } {
  // Seed from the SWR cache so a remount (e.g. navigating back to the
  // dashboard) paints the last-known data instantly instead of a spinner.
  const [data, setData] = useState<T | null>(() => getCached<T>(path) ?? null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const body = await apiFetch<T>(shopify, path);
        if (cancelled) return;
        setData(body);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    tick();
    const id = setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // streamTick is a dependency so a bump from useDashboardStream
    // re-runs this effect — same as if intervalMs had changed. The
    // effect's first action is tick(), which is the fetch we want.
  }, [shopify, path, intervalMs, streamTick]);

  return { data, error, loading };
}

// ---------- Refresh ticks ----------
//
// The SSE "real-time" stream was removed (audit F1): EventSource cannot send
// the App Bridge Authorization header, so every connection 401'd against
// session-auth and the dashboard silently ran on its polling safety net the
// whole time — the stream was dead code presenting as a feature. Polling
// (10-60s per card) IS the refresh mechanism. streamTick survives as a
// constant so the poll hooks keep a single "bump to refetch" seam; if a real
// push channel ever lands (an authed fetch + ReadableStream), it plugs in
// here without touching the cards.
const STREAM_TICK_DISABLED = 0;

// ---------- Types mirroring the backend response shapes ----------

// Mirrors GET /api/dashboard/sales — purchase value Datacrow forwarded
// server-side over the last 30 days, with the prior 30 for a trend.
interface SalesResponse {
  windowDays: number;
  value: number;
  orders: number;
  currency: string | null;
  previousValue: number;
  previousOrders: number;
}

interface ServerCaughtResponse extends ServerCaughtCounts {
  windowDays: number;
  missedValue: number;
  currency: string | null;
}

type HealthStatus = "green" | "yellow" | "red";

interface HealthResponse {
  status: HealthStatus;
  // Delivery paused by the plan gate (review W31), and the gate's reason
  // (review R4). Optional: an older backend mid-deploy doesn't send them.
  paused?: boolean;
  pausedReason?: string | null;
  checks: {
    eventsFlowing: { status: HealthStatus; deliveriesLastHour: number };
    successRate: {
      status: HealthStatus;
      rate: number | null;
      successCount: number;
      failureCount: number;
      windowHours: number;
    };
    consumerLag: {
      status: HealthStatus;
      backlogSeconds: number | null;
      secondsSinceLastDelivery: number | null;
      latestAttemptAt: string | null;
    };
  };
}

// Mirrors src/lib/meta-emq.ts EmqResult shape, plus the "not_configured"
// state that the route synthesizes when no Meta destination is saved.
// Discriminated union so render code can match on status exhaustively
// rather than nullable-juggling.
type MatchQualityResponse =
  | { status: "not_configured"; message: string }
  | { status: "no_data"; fetchedAt: string }
  | { status: "auth_error"; message: string; fetchedAt?: string }
  | { status: "upstream_error"; message: string; fetchedAt?: string }
  | {
      status: "ok";
      events: Array<{ event_name: string; score: number }>;
      fetchedAt: string;
    };

// The catalog already exports this map (its `short`, the same name the
// Destinations page puts on the card, so a dashboard banner and the card a
// merchant clicks through to cannot disagree). Re-deriving it here was a
// second copy, and the cast it needed was what hid a missing key.
const DESTINATION_LABEL = DESTINATION_DISPLAY_NAMES;

// ---------- Dashboard ----------

// Soft, organic backdrop behind the dashboard cards: instead of one
// directional band, four overlapping radial colour pools — teal up top, peach
// toward the bottom — blend into a freeform, mesh-like wash over a neutral
// base. Sizes and positions are deliberately uneven so it reads as organic
// rather than symmetric. Kept very light so the white cards stay crisp and the
// app still feels native to Shopify admin.
const DASHBOARD_BACKDROP: CSSProperties = {
  background: [
    "radial-gradient(58% 52% at 11% 16%, #d2efed 0%, rgba(210,239,237,0) 60%)",
    "radial-gradient(50% 46% at 82% 26%, #e3f3e7 0%, rgba(227,243,231,0) 56%)",
    "radial-gradient(64% 60% at 87% 82%, #fcdfd0 0%, rgba(252,223,208,0) 62%)",
    "radial-gradient(54% 54% at 17% 91%, #fbece0 0%, rgba(251,236,224,0) 58%)",
    "#f2f1ed",
  ].join(", "),
  borderRadius: 16,
  padding: 20,
};

export function Dashboard({
  shop,
  destinations,
  entitlement,
  availableTypes,
  onOpenBilling,
  onOpenDestinations,
  onOpenDataRequests,
}: {
  shop: string;
  destinations: DestinationView[];
  // The shop's own destination list, straight from /api/destinations. Only
  // the connect-more nudge reads it, and only for its denominator.
  availableTypes?: string[];
  // Optional — when absent (legacy callers, previews), the paywall banner
  // is skipped. App.tsx always supplies it in the real flow.
  entitlement?: EntitlementInfo;
  // Optional so existing callers (tests, previews) don't break. Used by the
  // paywall banner's in-content "Manage billing" button — App.tsx wires it to
  // navigate to <Billing/>. Top-level nav lives in the App Bridge NavMenu
  // (admin left sidebar), not the page header.
  onOpenBilling?: () => void;
  // Same shape as onOpenBilling. Used by the "Add destinations" CTA in the
  // Notifications panel — App.tsx wires it to navigate to <Destinations/>.
  onOpenDestinations?: () => void;
  // The "Open data requests" action on a waiting customer data request
  // (review W29); App.tsx navigates to <DataRequests/>.
  onOpenDataRequests?: () => void;
}) {
  const shopify = useAppBridge();
  // One SSE connection per Dashboard mount; cards consume the
  // streamTick counter to know when to refetch. Match-quality stays
  // on plain polling since Meta updates EMQ on its own slow cadence
  // — pushing it on every delivery would over-fetch from Meta.
  const streamTick = STREAM_TICK_DISABLED;

  return (
    <Page title="Datacrow" subtitle={shop}>
      <div style={DASHBOARD_BACKDROP}>
        <BlockStack gap="400">
          {entitlement?.blocked && (
            <PaywallBanner
              reason={entitlement.reason}
              onOpenBilling={onOpenBilling}
            />
          )}
          <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
            <SalesSentCard shopify={shopify} streamTick={streamTick} />
            <MatchQualityCard shopify={shopify} />
            <DataHealthCard shopify={shopify} streamTick={streamTick} />
          </InlineGrid>
          <Layout>
            <Layout.Section>
              <WeeklyEventsCard shopify={shopify} streamTick={streamTick} />
            </Layout.Section>
            <Layout.Section variant="oneThird">
              <BlockStack gap="400">
                <ServerCaughtCard
                  shopify={shopify}
                  streamTick={streamTick}
                  paused={entitlement?.blocked ?? false}
                  pausedReason={entitlement?.blocked ? entitlement.reason : null}
                />
                <NotificationsPanel
                  shopify={shopify}
                  destinations={destinations}
                  availableTypes={availableTypes}
                  onOpenDestinations={onOpenDestinations}
                  onOpenDataRequests={onOpenDataRequests}
                />
              </BlockStack>
            </Layout.Section>
          </Layout>
          <ActivityFeed heading="Recent activity" />
        </BlockStack>
      </div>
    </Page>
  );
}

// ---------- Paywall banner ----------
//
// Renders only when the backend tells us the store is blocked. Copy varies
// by reason so the merchant knows whether to re-approve a charge, update
// a frozen payment method, or just resubscribe. The CTA always goes to
// the Billing settings page since that's where any of those fixes start.
// The wording lives in dashboard-copy.ts (paywallCopy), which is tested.
function PaywallBanner({
  reason,
  onOpenBilling,
}: {
  reason: string;
  onOpenBilling?: () => void;
}) {
  // Critical tone (red): clear lapse the merchant needs to act on.
  // Warning tone (yellow): self-recoverable but data flow is paused.
  // We deliberately don't include subscription details here — the
  // Billing page has the rich version.
  const copy = paywallCopy(reason);

  return (
    <Banner tone={copy.tone} title={copy.title}>
      <BlockStack gap="200">
        <p>{copy.body}</p>
        {onOpenBilling && (
          <InlineStack>
            <Button variant="primary" onClick={onOpenBilling}>
              {copy.cta}
            </Button>
          </InlineStack>
        )}
      </BlockStack>
    </Banner>
  );
}

// ---------- Card 1: Sales sent to ad platforms ----------
//
// The DTC-facing "is this making me money?" headline. Purchase value Datacrow
// forwarded server-side over the last 30 days, with the trend vs the prior 30.
// Honest framing: we report value actually sent (delivered to >= 1 platform),
// not a fabricated "recovered" number.

function SalesSentCard({
  shopify,
  streamTick,
}: {
  shopify: Shopify;
  streamTick: number;
}) {
  const { data, error, loading } = usePoll<SalesResponse>(
    shopify,
    "/api/dashboard/sales",
    60_000,
    streamTick,
  );

  const value = data?.value ?? 0;
  const prev = data?.previousValue ?? 0;
  const pct = prev > 0 ? Math.round(((value - prev) / prev) * 100) : null;

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Sales sent to your ad platforms
        </Text>
        {loading && !data ? (
          <Spinner accessibilityLabel="Loading" size="small" />
        ) : error && !data ? (
          <Banner tone="warning">
            <p>Couldn't load sales: {error}</p>
          </Banner>
        ) : (
          <BlockStack gap="150">
            <InlineStack gap="200" blockAlign="baseline">
              <Text as="span" variant="heading2xl">
                {formatMoney(value, data?.currency ?? null)}
              </Text>
              {pct !== null ? (
                <Text
                  as="span"
                  variant="bodyMd"
                  tone={pct >= 0 ? "success" : "critical"}
                >
                  {pct >= 0 ? "▲" : "▼"} {Math.abs(pct)}% vs prior 30 days
                </Text>
              ) : null}
            </InlineStack>
            <Text as="p" tone="subdued" variant="bodySm">
              {(data?.orders ?? 0).toLocaleString()} purchases sent straight to
              your platforms&rsquo; servers in the last 30 days, independent of
              the browser pixel.
            </Text>
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

// Currency-format a headline amount; whole units (no cents) read cleaner at
// heading scale. Falls back to a plain number if the currency code is unusual.
// ---------- Card: Caught by the server ----------
//
// The problem-aware pitch as a live fact: confirmed orders the browser pixel
// never reported, delivered server-side anyway. Dollars first (outcome
// language), with an honesty footnote since the count includes orders a
// pixel can never see (POS, drafts, subscriptions), not only blockers.
function ServerCaughtCard({
  shopify,
  streamTick,
  paused,
  pausedReason,
}: {
  shopify: Shopify;
  streamTick: number;
  // The plan gate pauses delivery (review S8): the card must not read as
  // an all-clear under the paywall banner.
  paused: boolean;
  // The gate's reason, so the paused note names its own fix (review R4).
  pausedReason: string | null;
}) {
  const { data, error, loading } = usePoll<ServerCaughtResponse>(
    shopify,
    "/api/dashboard/server-caught",
    60_000,
    streamTick,
  );

  const summary = data ? serverCaughtSummary(data, paused, pausedReason) : null;
  let body;
  if (loading && !data) {
    body = <Spinner accessibilityLabel="Loading" size="small" />;
  } else if (error && !data) {
    body = (
      <Banner tone="warning">
        <p>Couldn't load this card: {error}</p>
      </Banner>
    );
  } else if (!data || !summary || summary.kind === "no_orders") {
    body = (
      <Text as="p" tone="subdued">
        No confirmed orders in the last 30 days yet. Once orders flow, this
        card shows the sales your browser pixel missed.
      </Text>
    );
  } else if (summary.kind === "all_seen") {
    body = (
      <BlockStack gap="150">
        <Text as="p">
          Your browser pixel saw every order in the last 30 days.
        </Text>
        <Text as="p" tone="subdued" variant="bodySm">
          The server-side copy is there the moment that changes.
        </Text>
      </BlockStack>
    );
  } else if (summary.kind === "not_caught") {
    body = <Text as="p">{summary.note}</Text>;
  } else {
    body = (
      <BlockStack gap="150">
        <Text as="span" variant="heading2xl">
          {formatMoney(data.missedValue, data.currency)}
        </Text>
        <Text as="p" tone="subdued" variant="bodySm">
          {data.missedOrders.toLocaleString()} of{" "}
          {data.totalOrders.toLocaleString()} orders in the last 30 days were
          invisible to browser tracking. Datacrow delivered them to your ad
          platforms anyway.
        </Text>
        <Text as="p" tone="subdued" variant="bodySm">
          Includes orders a pixel can never see, like POS, drafts, and
          subscriptions.
        </Text>
        {summary.note && (
          <Text as="p" variant="bodySm">
            {summary.note}
          </Text>
        )}
      </BlockStack>
    );
  }

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Caught by the server
        </Text>
        {body}
      </BlockStack>
    </Card>
  );
}

function formatMoney(value: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency ?? "USD",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${Math.round(value).toLocaleString()} ${currency ?? ""}`.trim();
  }
}

// ---------- Card: Events delivered this week vs last (bar chart) ----------

interface WeeklyResponse {
  days: Array<{ label: string; date: string; thisWeek: number; lastWeek: number }>;
}

// "2026-06-23" -> "6/23". String math avoids the timezone drift you'd get from
// parsing the ISO date and formatting it in the browser's locale.
function shortDate(iso: string): string {
  const [, mo, da] = iso.split("-");
  return mo && da ? `${Number(mo)}/${Number(da)}` : iso;
}

function WeeklyEventsCard({
  shopify,
  streamTick,
}: {
  shopify: Shopify;
  streamTick: number;
}) {
  const { data, error, loading } = usePoll<WeeklyResponse>(
    shopify,
    "/api/dashboard/events-weekly",
    60_000,
    streamTick,
  );

  const days = data?.days ?? [];
  const thisTotal = days.reduce((s, d) => s + d.thisWeek, 0);
  const lastTotal = days.reduce((s, d) => s + d.lastWeek, 0);
  const pctChange =
    lastTotal > 0 ? Math.round(((thisTotal - lastTotal) / lastTotal) * 100) : null;
  // Peak across both weeks scales the bars; min 1 avoids divide-by-zero on a
  // fresh store. Hex fallbacks (not Polaris vars) match DestinationBar/HealthDot.
  const max = Math.max(1, ...days.flatMap((d) => [d.thisWeek, d.lastWeek]));

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            Events delivered
          </Text>
          <InlineStack gap="300" blockAlign="center">
            <LegendSwatch color="#2b9b83" label="This week" />
            <LegendSwatch color="#c9cccf" label="Last week" />
          </InlineStack>
        </InlineStack>

        {loading && !data ? (
          <Spinner accessibilityLabel="Loading" size="small" />
        ) : error && !data ? (
          <Banner tone="warning">
            <p>Couldn't load weekly stats: {error}</p>
          </Banner>
        ) : (
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="baseline">
              <Text as="span" variant="heading2xl">
                {thisTotal.toLocaleString()}
              </Text>
              {pctChange !== null ? (
                <Text
                  as="span"
                  variant="bodyMd"
                  tone={pctChange >= 0 ? "success" : "critical"}
                >
                  {pctChange >= 0 ? "▲" : "▼"} {Math.abs(pctChange)}% vs last week
                </Text>
              ) : null}
            </InlineStack>
            <Text as="p" tone="subdued" variant="bodySm">
              delivered to all destinations in the last 7 days
            </Text>

            <div
              style={{ display: "flex", gap: 8, alignItems: "flex-end" }}
              aria-label={`Events delivered per day, this week vs last week. Peak ${max}.`}
            >
              {days.map((d, i) => (
                <div
                  key={d.date}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 3,
                      alignItems: "flex-end",
                      height: 76,
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      title={`${d.label} last week: ${d.lastWeek}`}
                      style={{
                        width: "42%",
                        height: Math.max(2, Math.round((d.lastWeek / max) * 76)),
                        background: "#c9cccf",
                        borderRadius: 2,
                      }}
                    />
                    <div
                      title={`${d.label} this week: ${d.thisWeek}`}
                      style={{
                        width: "42%",
                        height: Math.max(2, Math.round((d.thisWeek / max) * 76)),
                        background: "#2b9b83",
                        borderRadius: 2,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      lineHeight: 1.15,
                    }}
                  >
                    <Text
                      as="span"
                      variant="bodySm"
                      tone={i === days.length - 1 ? undefined : "subdued"}
                      fontWeight={i === days.length - 1 ? "semibold" : undefined}
                    >
                      {i === days.length - 1 ? "Today" : d.label}
                    </Text>
                    <Text as="span" variant="bodyXs" tone="subdued">
                      {shortDate(d.date)}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <InlineStack gap="100" blockAlign="center">
      <span
        style={{
          width: 10,
          height: 10,
          background: color,
          borderRadius: 2,
          display: "inline-block",
        }}
      />
      <Text as="span" variant="bodySm" tone="subdued">
        {label}
      </Text>
    </InlineStack>
  );
}

// ---------- Notifications panel ----------
//
// Right-hand rail listing things the merchant should know about. Items that
// are genuinely actionable (a destination stuck in test mode) get a "Needs
// attention" badge; the rest are informational nudges. Empty state is a
// healthy all-clear. Match quality is deliberately NOT re-flagged here — the
// match-quality card on the same screen already carries that signal, and a
// second amber/red flag for the same condition read as two alarms on a
// healthy store (one signal, one place).

function AttentionDot({ color }: { color: string }) {
  return (
    <span
      style={{
        width: 10,
        height: 10,
        minWidth: 10,
        background: color,
        borderRadius: "50%",
        display: "inline-block",
      }}
    />
  );
}

// Customer data requests are re-read every 5 minutes: they arrive a few
// times a year, and the list is uncached (it names shoppers), so each poll
// is a real request.
const DATA_REQUESTS_POLL_MS = 5 * 60_000;

function NotificationsPanel({
  shopify,
  destinations,
  availableTypes,
  onOpenDestinations,
  onOpenDataRequests,
}: {
  shopify: Shopify;
  destinations: DestinationView[];
  // The shop's own destination list from /api/destinations. Undefined on an
  // older cached payload, which allTypesFrom() reads as the non-hidden set.
  availableTypes?: string[];
  onOpenDestinations?: () => void;
  onOpenDataRequests?: () => void;
}) {
  const dataRequests = usePoll<DataRequestList>(shopify, "/api/data-requests", DATA_REQUESTS_POLL_MS);
  type Item = {
    dot: string;
    title: string;
    body: string;
    // Genuinely actionable items get a "Needs attention" badge; purely
    // informational ones (e.g. the connect-more nudge) don't.
    needsAttention?: boolean;
    action?: { label: string; onAction: () => void };
  };
  const items: Item[] = [];

  // A customer asked for their data and no file has been built yet
  // (review W29). First: it carries a legal deadline.
  const waiting = waitingRequests(dataRequests.data?.requests ?? []);
  if (waiting.length > 0) {
    const due = formatDate(waiting[0]!.answerBy);
    items.push({
      dot: "#e1a200",
      needsAttention: true,
      title:
        waiting.length === 1
          ? "A customer asked for their data"
          : `${waiting.length} customers asked for their data`,
      body:
        waiting.length === 1
          ? `Download the file, review it, and send it to them by ${due}.`
          : `Download the files, review them, and send them out. The first is due ${due}.`,
      action: onOpenDataRequests
        ? { label: "Open data requests", onAction: onOpenDataRequests }
        : undefined,
    });
  }

  // Destinations stuck in dry-run (logged, not sent) — the silent footgun.
  for (const d of destinations.filter((x) => x.configured && x.dryRun)) {
    items.push({
      dot: "#e1a200",
      needsAttention: true,
      title: `${DESTINATION_LABEL[d.type]} is in test mode`,
      body: "Events are logged, not sent. Set its *_DRY_RUN flag to false on both services to go live.",
    });
  }

  // Not all destinations connected yet. Counted against what THIS shop may
  // use, not against the catalog: the five types added on 2026-10-18 ship at
  // release "hidden" and reach nobody, so a catalog-length denominator read
  // "6 of 11" to a merchant whose picker offers six. allTypesFrom() is the
  // same per-shop list the Destinations page builds its cards from.
  const configuredCount = destinations.filter((d) => d.configured).length;
  const connectMore = connectMoreCard(configuredCount, allTypesFrom(availableTypes).length);
  if (connectMore) {
    items.push({
      dot: "#00344f",
      title: connectMore.title,
      body: connectMore.body,
      action: onOpenDestinations
        ? { label: "Add destinations", onAction: onOpenDestinations }
        : undefined,
    });
  }

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Notifications
        </Text>
        {items.length === 0 ? (
          <InlineStack gap="150" blockAlign="center">
            <AttentionDot color="#2b9b83" />
            <Text as="p" tone="subdued">
              No notifications. Everything looks healthy.
            </Text>
          </InlineStack>
        ) : (
          <BlockStack gap="300">
            {items.map((item, i) => (
              <BlockStack key={item.title} gap="300">
                {i > 0 ? <Divider /> : null}
                <BlockStack gap="100">
                  <InlineStack gap="150" blockAlign="center" wrap={false}>
                    <AttentionDot color={item.dot} />
                    <Text as="h3" variant="headingSm">
                      {item.title}
                    </Text>
                  </InlineStack>
                  {item.needsAttention ? (
                    <InlineStack>
                      <Badge tone="attention">Needs attention</Badge>
                    </InlineStack>
                  ) : null}
                  <Text as="p" tone="subdued" variant="bodySm">
                    {item.body}
                  </Text>
                  {item.action ? (
                    <InlineStack>
                      <Button variant="plain" onClick={item.action.onAction}>
                        {item.action.label}
                      </Button>
                    </InlineStack>
                  ) : null}
                </BlockStack>
              </BlockStack>
            ))}
          </BlockStack>
        )}
      </BlockStack>
    </Card>
  );
}

// ---------- Card 2: Meta event match quality ----------

function MatchQualityCard({ shopify }: { shopify: Shopify }) {
  const { data, error, loading } = usePoll<MatchQualityResponse>(
    shopify,
    "/api/dashboard/match-quality",
    5 * 60_000, // 5-minute cadence — Meta updates EMQ over hours, not seconds
  );

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Reaching the right people
        </Text>
        {loading && !data ? (
          <Spinner accessibilityLabel="Loading" size="small" />
        ) : error && !data ? (
          <Banner tone="warning">
            <p>Couldn't load match quality: {error}</p>
          </Banner>
        ) : data ? (
          <MatchQualityBody data={data} />
        ) : null}
      </BlockStack>
    </Card>
  );
}

// Per-state body for the "reaching the right people" card. Leads with the
// plain-English verdict; the raw EMQ score + per-event breakdown live in a
// collapsed technical-detail drawer (DTC: outcome first, jargon on demand).
function MatchQualityBody({ data }: { data: MatchQualityResponse }) {
  if (data.status === "not_configured") {
    return (
      <BlockStack gap="200">
        <Badge tone="attention">Not connected</Badge>
        <Text as="p" tone="subdued">
          Connect Meta to see how well your ads are reaching the right people.
        </Text>
      </BlockStack>
    );
  }
  if (data.status === "no_data") {
    return (
      <BlockStack gap="200">
        <Badge tone="info">Warming up</Badge>
        <Text as="p" tone="subdued">
          Meta is still scoring how well your shopper data matches real
          accounts. This appears a few hours after your first events flow.
        </Text>
      </BlockStack>
    );
  }
  if (data.status === "auth_error") {
    return (
      <BlockStack gap="200">
        <Badge tone="critical">Action needed</Badge>
        <Text as="p" tone="subdued">{data.message}</Text>
      </BlockStack>
    );
  }
  if (data.status === "upstream_error") {
    return (
      <BlockStack gap="200">
        <Badge tone="warning">Temporarily unavailable</Badge>
        <Text as="p" tone="subdued">
          Couldn&rsquo;t reach Meta right now. This usually clears on its own.
        </Text>
      </BlockStack>
    );
  }

  // status === "ok" — lead with the verdict, demote the number. The headline
  // reflects the PURCHASE event: that's what ad platforms optimize and bill
  // against, and the score this card exists to vouch for. Low-identifier
  // events (page_view, add_to_cart) structurally score low, so the old
  // min-based headline dragged healthy stores to a red "Needs work" — a
  // false alarm in the hero card. Weak events still surface in the drawer,
  // worst-first. No purchase scored yet -> median as the honest fallback.
  const purchase = data.events.find((e) => e.event_name.toLowerCase() === "purchase");
  const sorted = data.events.map((e) => e.score).sort((a, b) => a - b);
  const median =
    sorted.length === 0
      ? 0
      : (sorted[(sorted.length - 1) >> 1]! + sorted[sorted.length >> 1]!) / 2;
  const overall = purchase ? purchase.score : median;
  const tone =
    overall >= 8 ? "success" : overall >= 6 ? "info" : overall >= 4 ? "attention" : "critical";
  const verdict =
    overall >= 8 ? "Excellent" : overall >= 6 ? "Good" : overall >= 4 ? "Getting there" : "Needs work";
  const plain =
    overall >= 6
      ? "Meta is matching your shopper data to real accounts well, so your ads target and attribute accurately."
      : "Meta can't match enough of your shopper data to real accounts yet. That limits ad targeting and how much credit your ads get. Sending phone + address improves it.";
  return (
    <BlockStack gap="200">
      <InlineStack gap="200" blockAlign="center">
        <Text as="p" variant="headingLg">{verdict}</Text>
        <Badge tone={tone}>{`${overall.toFixed(1)}/10`}</Badge>
      </InlineStack>
      <Text as="p" tone="subdued" variant="bodySm">{plain}</Text>
      <TechnicalDetail label="Match quality by event">
        <Text as="p" tone="subdued" variant="bodySm">
          Meta scores how well each event&rsquo;s customer data matches real
          accounts, 0–10 (Needs work &lt; 4, Getting there 4–5.9, Good 6–7.9,
          Excellent 8+). The headline above reflects your purchase event, the
          one your ads optimize on.
        </Text>
        <BlockStack gap="100">
          {data.events
            .slice()
            .sort((a, b) => a.score - b.score)
            .map((ev) => (
              <InlineStack key={ev.event_name} align="space-between">
                <Text as="span" variant="bodySm">{ev.event_name}</Text>
                <Text as="span" variant="bodySm" tone={ev.score < 4 ? "critical" : "subdued"}>
                  {ev.score.toFixed(1)}
                </Text>
              </InlineStack>
            ))}
        </BlockStack>
      </TechnicalDetail>
    </BlockStack>
  );
}

// A collapsed "technical detail" disclosure — the agency / curious-owner
// drawer that keeps raw telemetry off the default DTC view but one click away.
function TechnicalDetail({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = `tech-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <BlockStack gap="150">
      <InlineStack>
        <Button
          variant="plain"
          disclosure={open ? "up" : "down"}
          onClick={() => setOpen((v) => !v)}
        >
          {label}
        </Button>
      </InlineStack>
      <Collapsible id={id} open={open}>
        <BlockStack gap="150">{children}</BlockStack>
      </Collapsible>
    </BlockStack>
  );
}

// ---------- Card 3: Data health ----------

function DataHealthCard({
  shopify,
  streamTick,
}: {
  shopify: Shopify;
  streamTick: number;
}) {
  const { data, error, loading } = usePoll<HealthResponse>(
    shopify,
    "/api/dashboard/health",
    30_000, // 30s — health is the most actionable signal, show fresh data
    streamTick,
  );

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Tracking reliability
        </Text>
        {loading && !data ? (
          <Spinner accessibilityLabel="Loading" size="small" />
        ) : error && !data ? (
          <Banner tone="warning">
            <p>Couldn't load health: {error}</p>
          </Banner>
        ) : data ? (
          <BlockStack gap="200">
            <InlineStack gap="200" blockAlign="center">
              <HealthDot status={data.status} />
              <Text as="p" variant="headingSm">
                {healthLabel(data)}
              </Text>
            </InlineStack>
            {/* Checks shown inline (not tucked behind a toggle) so the
                merchant always sees what's being measured. */}
            <HealthChecks data={data} />
          </BlockStack>
        ) : null}
      </BlockStack>
    </Card>
  );
}

// The three raw health checks (events flowing, success rate, consumer lag).
// Shown inline when unhealthy; tucked into the technical-detail drawer when
// green, so the default view just says "healthy."
function HealthChecks({ data }: { data: HealthResponse }) {
  return (
    <BlockStack gap="100">
      <HealthRow
        label={eventsFlowingLabel(data.paused, data.pausedReason, data.checks.eventsFlowing.deliveriesLastHour)}
        status={data.checks.eventsFlowing.status}
      />
      <HealthRow
        label={formatSuccessRate(data.checks.successRate)}
        status={data.checks.successRate.status}
      />
      {/* Skipped rows still move the last-attempt time, so "Last delivery:
          just now" would be false while paused. */}
      <HealthRow
        label={data.paused ? "Nothing is being sent while paused" : formatLag(data.checks.consumerLag)}
        status={data.checks.consumerLag.status}
      />
    </BlockStack>
  );
}

function HealthDot({ status }: { status: HealthStatus }) {
  // Plain-hex colors rather than Polaris CSS vars because (a) the tokens
  // for fill-success/caution/critical have shifted names between v12 and
  // v13 and silently fall back to empty, and (b) a dot is simple enough
  // that matching Polaris's exact shade isn't worth the dependency.
  const color =
    status === "green" ? "#2b9b83" : status === "yellow" ? "#b9770f" : "#b42318";
  return (
    <span
      role="img"
      aria-label={`Status: ${status}`}
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: color,
        flexShrink: 0,
      }}
    />
  );
}

function HealthRow({ label, status }: { label: string; status: HealthStatus }) {
  return (
    <InlineStack gap="150" blockAlign="center">
      <HealthDot status={status} />
      <Text as="span" variant="bodySm">
        {label}
      </Text>
    </InlineStack>
  );
}

function healthLabel(data: HealthResponse): string {
  // Plan gate: events are recorded, then skipped at every destination
  // (review W31). The events-flowing row says what lifts the pause, from
  // this poll's pausedReason (review R4).
  if (data.paused) return "Paused: events are recorded, not sent";
  if (data.status === "green") return "Healthy: every sale is being counted";
  // Name the ACTUAL problem. Delivery failures and a delivery delay are very
  // different things — never say "not delivering" when deliveries are
  // succeeding and merely queued behind a busy moment.
  const failing = data.checks.successRate.status !== "green";
  if (failing) {
    return data.status === "red"
      ? "Some events aren't delivering"
      : "Mostly healthy: some events are retrying";
  }
  // Success rate is fine, so the only thing not-green is the backlog: a delay.
  return data.status === "red"
    ? "Events are delayed: deliveries are catching up"
    : "Mostly healthy: a short delay";
}

function formatSuccessRate(r: HealthResponse["checks"]["successRate"]): string {
  if (r.rate === null) return "Success rate: no deliveries yet";
  const pct = Math.round(r.rate * 1000) / 10;
  return `Success rate: ${pct}% (${r.successCount}/${r.successCount + r.failureCount}, last ${r.windowHours}h)`;
}

function formatLag(l: HealthResponse["checks"]["consumerLag"]): string {
  // Guard every numeric field. During a rolling deploy the backend can briefly
  // return the older response shape, leaving these undefined — without the
  // guard the thresholds below fall through and we render "NaN d ago".
  const backlog = finiteOrNull(l.backlogSeconds);
  const sinceLast = finiteOrNull(l.secondsSinceLastDelivery);
  // Backed up (a real stall) → the actionable fact is how long events have
  // been waiting to send. This only fires when the worker is genuinely behind,
  // never on a normal quiet gap between orders.
  if (l.status !== "green" && backlog !== null) {
    return `Events waiting to send: ${formatDuration(backlog)}`;
  }
  // Caught up (quiet store included) → just show when we last delivered, as
  // neutral context. A long gap here is normal and stays green.
  if (sinceLast === null) return "No deliveries yet";
  if (sinceLast < 60) return "Last delivery: just now";
  return `Last delivery: ${formatDuration(sinceLast)} ago`;
}

// Coerce an API number that might be missing (older response shape) or NaN to
// a clean null, so the formatters never emit "NaN".
function finiteOrNull(n: number | null | undefined): number | null {
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} d`;
}
